import { Children, type FC, type PropsWithChildren, type ReactNode, forwardRef } from 'react';
import { DndContext, PointerSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, arrayMove, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { Plus } from 'lucide-react';
import { SortableFlagProvider } from '@/components/inspector/entity-fields/form/sortable-flag-contex';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useTranslations } from 'next-intl';

interface BaseOptionsProps {
  className?: string;
  addButtonClassName?: string;
  containerClassName?: string;
  disabled?: boolean;
  isLoading?: boolean;
  emptyState?: ReactNode;
  handleAddNewOption?: () => void;
  addButtonPosition?: 'top' | 'bottom';
  addButtonLabel?: string;
  readOnly?: boolean;
  fullHeight?: boolean;
}

// When sortable=true, require `order` and `onOrderChange`
interface SortableOptionsProps extends BaseOptionsProps {
  sortable: true;
  order: Array<string>;
  onOrderChange: (newOrder: Array<string>) => void;
}

// When sortable is false or omitted, disallow those props
interface NonSortableOptionsProps extends BaseOptionsProps {
  sortable?: false;
  order?: never;
  onOrderChange?: never;
}

export type OptionsProps = SortableOptionsProps | NonSortableOptionsProps;

export const Options: FC<PropsWithChildren<OptionsProps>> = forwardRef<HTMLDivElement, PropsWithChildren<OptionsProps>>(
  (
    {
      handleAddNewOption,
      className,
      addButtonClassName,
      containerClassName,
      disabled,
      isLoading,
      children,
      emptyState,
      addButtonPosition = 'bottom',
      addButtonLabel,
      readOnly,
      order,
      onOrderChange,
      sortable = false,
      fullHeight = false,
    },
    ref,
  ) => {
    const tOptions = useTranslations('inspector.options');
    const sensors = useSensors(useSensor(PointerSensor));

    const handleDragEnd = ({ active, over }: { active: any; over: any }) => {
      if (!sortable || !order || !onOrderChange) {
        return;
      }
      if (over && active.id !== over.id) {
        const oldIndex = order.indexOf(active.id);
        const newIndex = order.indexOf(over.id);
        onOrderChange(arrayMove(order, oldIndex, newIndex));
      }
    };

    // `w-0 min-w-full`: Radix wraps ScrollArea children in a `display:table` div sized to
    // content, so a `%` width can't cap a long unbroken value — it would grow past view instead of truncating.
    const content = <div className={cn('flex w-0 min-w-full flex-col', className)}>{children}</div>;

    return (
      <>
        {addButtonPosition === 'top' && handleAddNewOption && !readOnly && (
          <Button
            onClick={() => handleAddNewOption()}
            type="button"
            className={cn(
              'group/add-new-option dark:focus-visible:[&>svg]:bg-white/4 focus-visible:[&>svg]:bg-black/4 w-full justify-start rounded-none bg-ui-panel! px-2 py-1 text-icon-base dark:text-text-gray',
              addButtonClassName,
            )}
            disabled={disabled || isLoading || readOnly}
            data-cy="add-new-option-button"
            icon={Plus}
            iconClassName="icon-6 duration-150 rounded-md group-hover/add-new-option:stroke-neutral-700 dark:group-hover/add-new-option:stroke-neutral-100"
          >
            {addButtonLabel}
          </Button>
        )}
        {children && Children.count(children) > 0 ? (
          <ScrollArea
            ref={ref}
            className={cn(
              'flex w-full flex-col rounded-none bg-input-background transition-[height] duration-150',
              fullHeight ? 'h-full' : 'h-52',
              handleAddNewOption && addButtonPosition === 'bottom' && 'border border-b-0! border-ui-bevel',
              (disabled || isLoading) && 'pointer-events-none opacity-50',
              containerClassName,
            )}
          >
            {sortable && Array.isArray(order) && typeof onOrderChange === 'function' ? (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
                modifiers={[restrictToVerticalAxis]}
              >
                <SortableContext items={order} strategy={verticalListSortingStrategy}>
                  <SortableFlagProvider value={sortable}>{content}</SortableFlagProvider>
                </SortableContext>
              </DndContext>
            ) : (
              content
            )}
          </ScrollArea>
        ) : (
          <div
            className={cn(
              'flex w-full flex-col justify-center rounded-none bg-input-background transition-[height] duration-150',
              fullHeight ? 'h-full' : 'h-32',
              handleAddNewOption && addButtonPosition === 'bottom' && 'border border-b-0! border-ui-bevel',
              (disabled || isLoading) && 'pointer-events-none opacity-50',
              containerClassName,
            )}
          >
            {emptyState ?? (
              <p className="p-4 text-center text-balance text-icon-base">
                {tOptions.rich('empty', { plusIcon: () => <Plus className="inline icon-4" /> })}
              </p>
            )}
          </div>
        )}

        {/* Add a new empty option to the end of the list */}
        {addButtonPosition === 'bottom' && handleAddNewOption && !readOnly && (
          <Button
            onClick={() => handleAddNewOption()}
            type="button"
            className="group/add-new-option w-full justify-start rounded-none border border-t-0! border-ui-bevel bg-input-background! p-0"
            disabled={disabled || isLoading}
            data-cy="add-new-option-button"
          >
            <div className="group/add-new-option m-px w-full justify-start rounded-none border-0 border-t border-t-toolbar-bevel bg-[#e9e9e9] px-2 py-1 hover:bg-[#e9e9e9] dark:bg-[#232323] dark:hover:bg-[#232323]">
              <Plus className="icon-6 stroke-icon-base duration-150 group-hover/add-new-option:stroke-neutral-700 dark:group-hover/add-new-option:stroke-neutral-100" />
            </div>
          </Button>
        )}
      </>
    );
  },
);

Options.displayName = 'Options';
